/**
 * Cart Stock Guard — 카트 진입 시 sold-out / 재고 부족 라인 자동 제거.
 *
 * 흐름:
 *  1. SSR (cart-stock-guard.liquid) 가 문제 라인을 data-cart-stock-issues 에 마킹
 *  2. 이 스크립트가 마킹 읽어서 /cart/change.js 로 제거 (또는 가능 수량으로 조정)
 *  3. 모달 (cart-stock-alert-modal) 띄워서 사용자에게 안내
 *  4. 카트 섹션 silent refresh (cart.js 의 sectionsToRender 패턴 — cart-items / cart-drawer innerHTML 만 교체)
 *
 * 🔴 [중요] 회귀 방어선: HK_EC 단독 EC 채널 운영 정리 이후에도 race condition / Buy again 우회 대비.
 *          음수 재고 사고 회귀 case: ~/lukuku-dev-kei/projects/misto/wiki/docs/inventory-location-policy.md
 */

(function() {
  'use strict';

  // 🔴 [중요] 스크립트가 main-cart.liquid + cart-drawer.liquid 양쪽에 <script> 태그로 로드됨 →
  // IIFE 가 2번 실행 → init()/runGuard()/cart:updated listener 중복 → 같은 issue 로 /cart/update.js 두 번 호출 (두 번째는 422).
  // window 레벨 플래그로 한 번만 init.
  if (window.__cartStockGuardInit) return;
  window.__cartStockGuardInit = true;

  let isProcessing = false;

  function init() {
    runGuard();
    // 카트 섹션 re-render (Shopify sections-rendering) 후에도 재실행
    // 🟠 cart-checkout-hub.md 핵심 정책 — 카트 갱신 이벤트 SSOT = `cart:updated`. `cart:refresh` 는 미사용 (legacy)
    document.addEventListener('cart:updated', runGuard);
  }

  async function runGuard() {
    if (isProcessing) return;
    const issueEl = document.querySelector('[data-cart-stock-issues]');
    if (!issueEl) return;

    const raw = issueEl.dataset.cartStockIssues;
    if (!raw) return;

    const issues = parseIssues(raw);
    if (issues.length === 0) {
      issueEl.remove();
      return;
    }

    isProcessing = true;
    // 마킹 즉시 제거 (중복 실행 방지)
    issueEl.remove();

    try {
      // 🔴 [중요] /cart/change.js 는 솔드아웃 라인(inventoryPolicy=DENY + available=0)을 qty=0(삭제)로도
      // 거부함 → 422 "already sold out". /cart/update.js 의 updates 맵은 같은 라인을 거부 없이 처리한다.
      // 회귀 case: wiki/docs/cart-change-sold-out-422-trap.md
      const updates = {};
      issues.forEach(it => { updates[it.key] = it.maxAvailable; });
      const res = await fetch(MISTO.localeUrl('/cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        console.error('[cart-stock-guard] /cart/update.js failed:', res.status, errBody.slice(0, 200));
      }

      showModal(issues);
      await refreshCartSections();
    } catch (e) {
      console.error('[cart-stock-guard] failed:', e);
    } finally {
      isProcessing = false;
    }
  }

  function parseIssues(raw) {
    return raw.split('||').filter(Boolean).map(s => {
      const parts = s.split('|');
      return {
        key: parts[0],
        maxAvailable: parseInt(parts[1], 10) || 0,
        title: parts.slice(2).join('|')
      };
    });
  }

  function showModal(issues) {
    const modal = document.querySelector('[data-cart-stock-modal]');
    if (!modal) return;

    const list = modal.querySelector('[data-cart-stock-modal-list]');
    if (list) {
      list.innerHTML = '';
      issues.forEach(it => {
        const li = document.createElement('li');
        li.className = 'cart-stock-alert-modal__item';
        const title = document.createElement('span');
        title.className = 'cart-stock-alert-modal__item-title';
        title.textContent = it.title;
        const reason = document.createElement('span');
        reason.className = 'cart-stock-alert-modal__item-reason';
        reason.textContent = it.maxAvailable === 0
          ? (window.theme && theme.t && theme.t('cart.stock_alert_removed')) || 'Sold out'
          : `${(window.theme && theme.t && theme.t('cart.stock_alert_adjusted')) || 'Adjusted to'} ${it.maxAvailable}`;
        li.appendChild(title);
        li.appendChild(reason);
        list.appendChild(li);
      });
    }

    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    modal.querySelectorAll('[data-cart-stock-modal-close]').forEach(btn => {
      btn.addEventListener('click', closeModal, { once: true });
    });
  }

  function closeModal() {
    const modal = document.querySelector('[data-cart-stock-modal]');
    if (!modal) return;
    modal.hidden = true;
    document.body.style.overflow = '';
  }

  // 🔴 [중요] sections-rendering swap 시 wrapper(`#shopify-section-{id}`) 통째 innerHTML 교체 금지.
  //          cart-items custom element 재초기화 누락 → 카트 페이지 line-item stale.
  //          반드시 cart 인스턴스의 getSectionsToRender() 가 반환하는 selector(`#shopify-section-{id} cart-items` 등) 로 좁힐 것.
  //          회귀 case: wiki/docs/cart-section-swap-selector-trap.md
  async function refreshCartSections() {
    try {
      const cart = document.querySelector('cart-items') || document.querySelector('cart-drawer');
      const sections = cart?.getSectionsToRender?.() || [];
      if (sections.length === 0) return;
      const res = await fetch(MISTO.localeUrl(`/cart?sections=${sections.map(s => s.section).join(',')}`));
      const data = await res.json();
      const parser = new DOMParser();
      sections.forEach(section => {
        const target = document.querySelector(section.selector) || document.querySelector(section.id);
        if (!target) return;
        const html = data[section.section];
        if (!html) return;
        const next = parser.parseFromString(html, 'text/html').querySelector(section.selector);
        if (next) target.innerHTML = next.innerHTML;
      });
      // 🔴 헤더 .cart-count-badge + 드로어 .cart-drawer__title-counter 동기화.
      //    section swap 만으로는 #CartDrawer-Body 밖에 있는 타이틀 카운트와 헤더 카운트가 stale.
      if (typeof updateCartCounters === 'function') {
        await updateCartCounters();
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) {
      console.error('[cart-stock-guard] refresh failed:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
